import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { generateTokenForContentAccess } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { amplifyConfig } from '@alucio/aws-beacon-amplify'
import { PageData } from 'src/types/staticAssets';
import { GenerateTokenForContentAccessQuery } from '@alucio/aws-beacon-amplify/src/API';

type AllowedHeaders = 'Alucio-Action' | 'Alucio-Content-Bucket' | 'Alucio-Authorization'
type AdditionalHeaders = { [key in AllowedHeaders]?: string }

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  var binary = '';
  var bytes = [].slice.call(new Uint8Array(buffer));

  bytes.forEach((b) => { binary += String.fromCharCode(b) });

  return window.btoa(binary);
}

async function getImageFromCloudfront(key: string): Promise<string> {
  if (key) {
    const imgArrayData = await getContentFromCloudfront(key, {}, 'image');

    if (imgArrayData) {
      const base64Flag = 'data:image/png;base64,';
      const imageStr = base64Flag + arrayBufferToBase64(imgArrayData);
      return imageStr;
    }
  }
  return '';
}

/**
 * Utility function which creates a request for content from cloudfront, then returns an object url generated from the blob contained in the response
 * @param key path for content stored in the S3 bucket. E.g. thumbs are in the format {convertedFolderKey}{pageId}_thumb.png
 */
async function getImageObjectURLFromCloudfront(key: string): Promise<string> {
  const response = fetchContentFromCloudfront(key)
    .then((response) => response.blob())
    .then((blob) => {
      const objectURL = URL.createObjectURL(blob)
      return objectURL
    })
  return response
}

/**
 * Utility function which makes a generic fetch request to cloudfront and returns the response
 * @param key path for content stored in the S3 bucket. E.g. thumbs are in the format {convertedFolderKey}{pageId}_thumb.png
 */
async function fetchContentFromCloudfront(key: string): Promise<Response> {
  const authHeaders = await getAuthHeaders()
  const url = `/content/${key}`
  const response = fetch(url, {
    method: 'GET',
    headers: authHeaders,
  })
  return response
}

async function getContentFromCloudfront(
  key: string,
  additionalHeaders?: AdditionalHeaders,
  expectedContentType?: string,
): Promise<ArrayBuffer | undefined> {
  const authHeaders = await getAuthHeaders()
  const url = `/content/${key}`;
  const imgResponse = await fetch(url, {
    method: 'GET',
    headers: { ...authHeaders, ...additionalHeaders },
  });

  const contentType = imgResponse.headers.get('Content-Type') || '';
  if (expectedContentType && !contentType.startsWith(expectedContentType)) {
    console.warn('Unexpected content type:', contentType);
    return undefined;
  }

  return await imgResponse.arrayBuffer();
}

async function downloadRecordingFromCloudfront(
  tenantId: string | undefined,
  sessionId: string,
  fileName: string,
): Promise<void> {
  const bucket = amplifyConfig.aws_user_files_s3_bucket;
  const appsyncClient = generateClient<GenerateTokenForContentAccessQuery>();
  const { data } = await appsyncClient.graphql({
    query: generateTokenForContentAccess,
    variables: {
      documentId: 'recordings',
      documentVersionId: sessionId,
      authorizedPath: `${tenantId}/recordings/${sessionId}/1.mp4`,
      durationSeconds: 15,
    },
  });
  if (data.generateTokenForContentAccess) {
    const JWT = encodeURIComponent(data.generateTokenForContentAccess.token)

    const url = `/content/${tenantId}/recordings/${sessionId}/1.mp4?token=${JWT}&fileName=${fileName}&bucket=${bucket}`;

    var recording = document.createElement('a');
    recording.href = url;
    recording.target = '_blank'
    recording.click();
  }
}

async function fetchJsonFromCloudfront(jsonContentKey: string, disabledCache?: boolean):Promise<Response> {
  const authHeaders = await getAuthHeaders()
  const url = `/content/${jsonContentKey}`
  if (disabledCache) {
    // eslint-disable-next-line dot-notation
    authHeaders['Cache-Control'] = 'no-cache'
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...authHeaders,
      'Accept': 'application/json',
    },
  })
  return response
}

/**
 * Utility function which to retry a promise a number of times before throwing an error
 */
export const retryPromise = async (
  promise: Promise<any>,
  retries = 3,
  waitBetweenRetries = 5000,
  _debugId?: string,
) => {
  let error;
  for (let i = 0; i < retries; i++) {
    try {
      return await promise;
    } catch (e) {
      error = e;
      await new Promise((resolve) => setTimeout(resolve, waitBetweenRetries));
    }
  }
  throw error;
};

// FETCH THE CONTENT OF EACH PAGE'S DOC
export const fetchPagesJson = async (s3Keys: Record<string, string> | Array<string>, disableCache?: boolean): Promise<
  Array<{ documentVersionId: string, content: PageData }>
> => {
  return await Promise.all(
    Object
      .keys(s3Keys)
      .map(async (key) => {
        const response = await fetchJsonFromCloudfront(s3Keys[key], disableCache);
        const formattedResponse = await response.json();
        return {
          documentVersionId: key,
          content: formattedResponse as PageData,
        };
      }),
  );
};

export const fetchHtmlManifests = async (
  s3Keys: Record<string, string>,
  disableCache?: boolean,
): Promise<
  Array<{ documentVersionId: string, manifest?: { mode?: 'classic' | 'spa', entryHtmlUrl?: string } }>
> => {
  const getHtmlMode = (mode: unknown) => (mode === 'classic' || mode === 'spa' ? mode : undefined);

  return await Promise.all(
    Object
      .keys(s3Keys)
      .map(async (key) => {
        try {
          const response = await fetchJsonFromCloudfront(s3Keys[key], disableCache);
          if (!response.ok) {
            return { documentVersionId: key };
          }

          const manifest = await response.json();
          return {
            documentVersionId: key,
            manifest: {
              mode: getHtmlMode(manifest.mode),
              entryHtmlUrl: typeof manifest.entryHtmlUrl === 'string' ? manifest.entryHtmlUrl : undefined,
            },
          };
        } catch {
          return { documentVersionId: key };
        }
      }),
  );
};

// TODO: investigate a way to get the offline flag from app settings without having a parameter/property driling
const getAuthHeaders = async () => (navigator.onLine ? {
  'Alucio-Authorization': (await fetchAuthSession()).tokens?.idToken?.toString() || '',
  'Alucio-Content-Bucket': amplifyConfig.aws_user_files_s3_bucket,
} : {
  'Alucio-Authorization': '',
  'Alucio-Content-Bucket': amplifyConfig.aws_user_files_s3_bucket,
})

export {
  getImageFromCloudfront,
  getImageObjectURLFromCloudfront,
  fetchContentFromCloudfront,
  getContentFromCloudfront,
  downloadRecordingFromCloudfront,
  getAuthHeaders,
  fetchJsonFromCloudfront,
}
